import { Button } from '@progress/kendo-react-buttons';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Loader } from "@progress/kendo-react-indicators";
import { NumericTextBox, TextArea } from '@progress/kendo-react-inputs';
import { Error, Label } from '@progress/kendo-react-labels';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { JsonResponse } from 'TypeGen/json-response';
import CarrierAutoComplete from 'views/AssetCarriers/CarrierAutoComplete';
import DriverAutoComplete from 'views/AssetDrivers/DriverAutoComplete';
import VehicleAutoComplete from 'views/AssetVehicles/VehicleAutoComplete';
import CenterDivPanel from '../../components/CenterDivPanel';
import { fetchApi } from '../../services/api';
import CustomerInput from '../../components/CustomerInput';

type Props = {
  OrderID: number,
  CloseDialog: () => void,
  onSave?: (freightClaim: any) => void
};

type FreightClaimDialogModel = {
  DriverID?: number | null,
  VehicleID?: number | null,
  CarrierID?: number | null,
  OrderNumber?: number | string,
  CustomerID?: number | null,
  CustomerNumber?: string
};

const claimTypes = ['Cargo', 'Auto Liability', 'Property Damage'];

const FreightClaimDialog = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();

  const [driverId, setDriverId] = useState<number | null>(null);
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [carrierId, setCarrierId] = useState<number | null>(null);
  const [orderNumber, setOrderNumber] = useState<number | string | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerNumber, setCustomerNumber] = useState<string>('');

  const [claimType, setClaimType] = useState<string>(claimTypes[0]);
  const [claimAmount, setClaimAmount] = useState<number>(0);
  const [lossDate, setLossDate] = useState<Date | null>(null);
  const [openDate, setOpenDate] = useState<Date | null>(null); //new Date()
  const [customerClaimNumber, setCustomerClaimNumber] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  useEffect(() => {
    if (props.OrderID) {
      setLoading(true);
      fetchApi(`/api/Asset/FreightClaimDialogData/${props.OrderID}`)
        .then((response: FreightClaimDialogModel) => {
          setDriverId(response.DriverID ?? null);
          setVehicleId(response.VehicleID ?? null);
          setCarrierId(response.CarrierID ?? null);
          setOrderNumber(response.OrderNumber ?? null);
          setCustomerId(response.CustomerID ?? null);
          setCustomerNumber(response.CustomerNumber ?? '');
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          setError('Unable to load order data.');
        });
    }
  }, [props.OrderID]);

  const save = () => {
    setError(null);

    if (!driverId && !vehicleId && !carrierId) {
      setError('Driver, Vehicle, or Carrier is required.');
      return;
    }
    if (!openDate) {
      setError('Open Date is required.');
      return;
    }

    if (!lossDate) {
      setError('Loss Date is required.');
      return;
    }

    if (!customerId) {
      setError('Customer is required.');
      return;
    }

    if (!description || description.trim().length < 5) {
      setError('Description of loss is required and must be at least 5 characters.');
      return;
    }

    setSaving(true);

    const data = {
      DriverID: driverId,
      VehicleID: vehicleId,
      CarrierID: carrierId,
      OrderID: props.OrderID,
      ClaimType: claimType,
      ClaimAmount: claimAmount,
      OpenDate: openDate,
      LossDate: lossDate,
      CustomerClaimNumber: customerClaimNumber,
      CustomerID: customerId,
      Description: description
    };

    fetchApi('/api/Asset/FreightClaimAdd', data, 'POST')
      .then((response: JsonResponse) => {
        setSaving(false);
        if (response.Success) {
          const link = response.Links?.find(l => l.Name === 'FreightClaimProfile');
          if (link) history.push(link.Link);
          if (props.onSave) props.onSave(response);
          props.CloseDialog();
        } else {
          setError(response.ErrorMessage || 'Unable to save claim.');
        }
      })
      .catch(() => {
        setSaving(false);
        setError('Unable to save claim.');
      });
  };

  return (
    <Dialog title="New Cargo Claim" onClose={props.CloseDialog} width={500} height={window.innerHeight / 1.2}>
      {(loading || saving) && <CenterDivPanel>
        <Loader type="converging-spinner" />
      </CenterDivPanel>}
      <form className="k-form k-form-md p-0">
        <FieldWrapper>
          <Label>Order Number:</Label>
          <div>
            <strong>{orderNumber || props.OrderID}</strong>
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label>Claim Type:</Label>
          <select
            className="custom-select"
            value={claimType}
            onChange={e => setClaimType(e.target.value)}
          >
            {claimTypes.map(x =>
              <option key={x} value={x}>{x}</option>
            )}
          </select>
          {/*{claimType === null && <Error>Claim Type is required.</Error>}*/}
        </FieldWrapper>
        <FieldWrapper>
          <Label>Claim Amount:</Label>
          <NumericTextBox
            required
            min={0}
            format="c2"
            value={claimAmount}
            onChange={e => setClaimAmount(e.value)}
            width="100%"
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Customer Claim #:</Label>
          <input
            className="form-control"
            maxLength={50}
            value={customerClaimNumber}
            onChange={e => setCustomerClaimNumber(e.target.value)}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Open Date:</Label>
          <DatePicker
            required
            value={openDate}
            onChange={e => setOpenDate(e.value as Date)}
            format="MM/dd/yyyy"
            width="100%"
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Loss Date:</Label>
          <DatePicker
            required
            value={lossDate}
            onChange={e => setLossDate(e.value as Date)}
            format="MM/dd/yyyy"
            width="100%"
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Customer:</Label>
          <div className="input-group">
            <CustomerInput
              CustomerID={customerId ?? 0}
              CustomerNumber={customerNumber ?? ''}
              onChange={e => {
                setCustomerId(e.CustomerID || null);
              }}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label>Driver:</Label>
          <DriverAutoComplete
            selectedDriverID={driverId}
            onSelectedDriver={d => setDriverId(d?.DriverID || null)}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Vehicle:</Label>
          <VehicleAutoComplete
            selectedVehicleID={vehicleId}
            onSelectedVehicle={v => setVehicleId(v?.VehicleID || null)}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Carrier:</Label>
          <CarrierAutoComplete
            selectedCarrierID={carrierId}
            onSelectedCarrier={c => setCarrierId(c?.CarrierID || null)}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Description of Loss:</Label>
          <TextArea
            required
            value={description}
            onChange={e => setDescription(e.value)}
            rows={3}
            maxLength={400}
          />
        </FieldWrapper>
        {error && <Error>{error}</Error>}
      </form>
      <DialogActionsBar>
        <Button onClick={props.CloseDialog} disabled={saving || loading}>Cancel</Button>
        <Button themeColor="primary" onClick={save} disabled={saving || loading}>Create Cargo Claim</Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default FreightClaimDialog;